import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    Input,
    OnChanges,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { removeAmChartLogo } from 'app/core/shared/amchart4/amchart4.module';
import { ExpandModalDialog } from 'app/modules/well-integrity-management/component/expand-modal/expand-modal.component';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { AxisItemLocation } from '@amcharts/amcharts4/.internal/charts/axes/Axis';
import { RemedialProgressResponse } from '../../model/well-integrity-management.type';

am4core.useTheme(am4themes_animated);

@Component({
    selector: 'remedial-progress',
    templateUrl: './remedial-progress.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgTemplateOutlet, CommonModule],
    styleUrls: ['./remedial-progress.component.scss'],
})
export class RemedialProgressComponent implements OnChanges {
    @Input() expandIcon: any;
    @Input() data: RemedialProgressResponse;
    currentYear: number = new Date().getFullYear();

    constructor(
        private readonly dialog: MatDialog,
        private readonly elementRef: ElementRef,
        private readonly cdr: ChangeDetectorRef,
        private readonly destroyRef: DestroyRef,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['data'] && this.data?.data?.monthlyData) {
            this.createGroupBarChart();
            this.cdr.markForCheck();
        }
    }

    openDialog(title: string, componentName: string, functionName: string) {
        const component = document.getElementById(componentName);

        const dialogRef = this.dialog.open(ExpandModalDialog, {
            data: {
                title: title,
                viewComponent: component,
                functionName: functionName,
                apiData: this.data,
            },
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
                console.log(`Dialog result: ${result}`);
            });
    }

    createGroupBarChart() {
        const myGraphElement = document.getElementById('gorupedBarChart');
        if (!myGraphElement) return;

        myGraphElement.style.height = '45vh';
        myGraphElement.style.width = '100%';

        const chart = am4core.create('gorupedBarChart', am4charts.XYChart);
        const monthlyData = this.data?.data?.monthlyData || [];

        // Transform the data for the chart
        chart.data = monthlyData.map((item) => ({
            date: new Date(this.currentYear, item.month - 1, 1),
            baseline: item.baselineCount,
            emerging: item.emergingCount,
            general: item.generalCount,
            monthName: item.monthName,
        }));

        // Create axes
        const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        dateAxis.renderer.grid.template.location = 0;
        dateAxis.renderer.minGridDistance = 20;
        dateAxis.dateFormats.setKey('month', 'MMM');
        dateAxis.periodChangeDateFormats.setKey('month', 'MMM');
        dateAxis.renderer.labels.template.paddingTop = 20;
        dateAxis.renderer.labels.template.fontWeight = 'bolder';
        dateAxis.renderer.labels.template.fill = am4core.color('#000');

        chart.yAxes.push(new am4charts.ValueAxis());

        // Create series for each category
        const createSeries = (field: string, name: string, color: string) => {
            const series = chart.series.push(new am4charts.ColumnSeries());
            series.dataFields.valueY = field;
            series.dataFields.dateX = 'date';
            series.name = name;
            series.columns.template.tooltipText = '{dateX}: [b]{valueY}[/]';
            series.strokeWidth = 2;
            series.stacked = true;
            series.columns.template.strokeWidth = 0;
            series.fill = am4core.color(color);
        };

        createSeries('baseline', 'Baseline', '#008FD3');
        createSeries('emerging', 'Emerging', '#0065A5');
        createSeries('general', 'General', '#0A195F');

        // Create ranges for baseline and emerging
        const createRange = (from: Date, to: Date, label: string) => {
            const range = dateAxis.axisRanges.create();
            range.date = from;
            range.endDate = to;

            range.label.text = label;
            range.label.paddingTop = 0;
            range.label.rotation = -90;
            range.label.fontSize = '12px';
            range.label.fontWeight = '400';
            range.label.location = 0.2 as AxisItemLocation;
            range.label.horizontalCenter = 'middle';

            range.grid.disabled = true;
        };

        // For each month, create baseline/emerging markers
        // for (let i = 0; i < 12; i++) {
        //     const firstDay = new Date(this.currentYear, i, 1);

        //     // Mark a point in the month as reference
        //     createRange(firstDay, new Date(this.currentYear, i, 5), 'Month');
        // }

        const createRangeGrid = (date: Date) => {
            const range = dateAxis.axisRanges.create();
            range.date = date;
            range.grid.strokeOpacity = 0.2;
            range.tick.disabled = false;
            range.tick.strokeOpacity = 0.3;
            range.tick.length = 60;
        };

        for (let i = 0; i < 12; i++) {
            createRangeGrid(new Date(this.currentYear, i, 1));
        }
        createRangeGrid(new Date(this.currentYear + 1, 0, 1));

        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';
        chart.legend.valign = 'bottom';
        chart.legend.contentAlign = 'center';
        chart.legend.labels.template.fontSize = 12;
        chart.legend.labels.template.fill = am4core.color('#000');
        chart.legend.itemContainers.template.paddingTop = 0;
        chart.legend.itemContainers.template.paddingBottom = 0;
        chart.legend.marginTop = 20;

        // Custom marker style
        chart.legend.markers.template.width = 12;
        chart.legend.markers.template.height = 12;
        let markerTemplate = chart.legend.markers.template.children.getIndex(
            0,
        ) as am4core.RoundedRectangle;
        markerTemplate.cornerRadius(4, 4, 4, 4);

        removeAmChartLogo(this.elementRef.nativeElement);
    }
}
